import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { MaskedLines } from "@/components/motion/MaskedLines";
import { Reveal } from "@/components/motion/Reveal";
import { CHAPTERS } from "@/data/content";
import { EASE } from "@/lib/motion";

const ChapterImages = ({ active }) => (
  <div className="relative h-full overflow-hidden rounded-[2.5rem] bg-sand">
    {CHAPTERS.map((c, i) => (
      <motion.div key={c.n} className="absolute inset-0" style={{ zIndex: i }} initial={false} animate={{ clipPath: i <= active ? "inset(0% 0% 0% 0%)" : "inset(100% 0% 0% 0%)" }} transition={{ duration: 1.1, ease: EASE }}>
        <motion.img src={c.image} alt={c.alt} className="h-full w-full object-cover" style={{ objectPosition: c.position }} animate={{ scale: i === active ? 1.04 : 1.14 }} transition={{ duration: 1.6, ease: EASE }} />
      </motion.div>
    ))}
    <div className="absolute inset-x-0 bottom-0 z-10 flex items-end justify-between bg-gradient-to-t from-navy/70 to-transparent p-8">
      <span className="font-brand text-7xl text-ivory" data-testid="chapter-active-numeral">{CHAPTERS[active].n}</span>
      <span className="text-[13px] uppercase tracking-[0.3em] text-ivory">{CHAPTERS[active].eyebrow}</span>
    </div>
  </div>
);

const ChapterText = ({ chapter, index, onActive }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-45% 0px -45% 0px" });

  useEffect(() => {
    if (inView) onActive(index);
  }, [inView, index, onActive]);

  return (
    <article ref={ref} data-testid={`chapter-${chapter.n}`} className="flex flex-col justify-center py-10 lg:min-h-[78vh]">
      <div className="mb-8 overflow-hidden rounded-[2rem] lg:hidden">
        <img src={chapter.image} alt={chapter.alt} className="aspect-[4/5] w-full object-cover" style={{ objectPosition: chapter.position }} loading="lazy" />
      </div>
      <Reveal>
        <div className="flex items-baseline gap-5">
          <span className="font-brand text-4xl text-copper">{chapter.n}</span>
          <span className="eyebrow">{chapter.eyebrow}</span>
        </div>
        <h3 className="mt-6 font-display text-[2rem] font-medium leading-[1.08] text-navy sm:text-4xl lg:text-5xl">{chapter.title}</h3>
        <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate2">{chapter.text}</p>
      </Reveal>
    </article>
  );
};

export const Chapters = () => {
  const [active, setActive] = useState(0);
  return (
    <section id="storia" className="relative bg-ivory pb-16 pt-8 lg:pb-28" data-testid="story-section">
      <div className="container-pf">
        <div className="max-w-3xl">
          <p className="eyebrow">La nostra storia</p>
          <h2 className="mt-5 font-display text-4xl font-medium leading-[1.02] text-navy sm:text-5xl lg:text-6xl">
            <MaskedLines lines={["Una linea nata per", <em className="italic text-copper">la pelle che ha vissuto.</em>]} />
          </h2>
        </div>
        <div className="mt-14 grid gap-6 lg:mt-20 lg:grid-cols-12 lg:gap-12">
          <div className="hidden lg:col-span-6 lg:block">
            <div className="sticky top-36 h-[calc(100vh-11rem)]">
              <ChapterImages active={active} />
            </div>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            {CHAPTERS.map((c, i) => (
              <ChapterText key={c.n} chapter={c} index={i} onActive={setActive} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
